import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import Slot from '../../components/Slot';
import useEventListener from '../../utils/useEventListener';
import { useQuillReadOnly } from '../QuillContext';
import { useUIElementResize } from '../UIElementResizeContext';
import useInternalCopyPaste from '../utils/useInternalCopyPaste';
import * as styles from './ImageCaption.module.scss';

const LINE_BREAK_REGEX = /\r?\n|\r/g;
const PLACEHOLDER = 'Add a caption';
const CAPTION_MAX_LENGTH = 250;

interface ImageCaptionProps {
  caption: string;
  onCaptionChange: (caption: string) => void;
  minHeight?: number;
}

const ImageCaption = React.forwardRef<HTMLDivElement, ImageCaptionProps>(
  ({ caption, onCaptionChange, minHeight }, ref) => {
    const readOnly = useQuillReadOnly();
    const captionRef = useRef<HTMLElement | null>(null);
    const [textareaNode, setTextareaNode] =
      useState<HTMLTextAreaElement | null>(null);
    const [editingHeight, setEditingHeight] = useState<number | undefined>(
      undefined,
    );

    const uiElementResize = useUIElementResize();
    useEffect(() => () => uiElementResize(), [uiElementResize, minHeight]);

    const setElement = useInternalCopyPaste();

    useEventListener(textareaNode, 'keydown', (event) => {
      // Let Quill handle these keys
      if (!['ArrowUp', 'ArrowDown', 'Enter'].includes(event.key)) {
        event.stopPropagation();
      }
    });

    const handleChange: React.ChangeEventHandler<HTMLTextAreaElement> = (
      event,
    ) =>
      onCaptionChange(
        event.target.value.replace(LINE_BREAK_REGEX, ' ').slice(
          0,
          // Backward compatibility - do not restrict length, if caption was already too long
          caption.length > CAPTION_MAX_LENGTH ? undefined : CAPTION_MAX_LENGTH,
        ),
      );

    const onFocus = () => setEditingHeight(captionRef.current?.clientHeight);
    const onBlur = () => setEditingHeight(undefined);

    const shouldShowTextArea = !readOnly;
    return (
      <Slot ref={captionRef}>
        <Slot ref={ref}>
          <div
            ref={setElement}
            className={classNames(styles.captionContainer, {
              [styles.empty]: !caption,
            })}
            style={{ height: editingHeight, minHeight }}
          >
            <figcaption
              className={classNames(styles.caption, {
                [styles.hide]: shouldShowTextArea,
              })}
            >
              {caption || (shouldShowTextArea ? PLACEHOLDER : <>&nbsp;</>)}
            </figcaption>
            {shouldShowTextArea && (
              <textarea
                onFocus={onFocus}
                onBlur={onBlur}
                ref={setTextareaNode}
                className={styles.caption}
                value={caption}
                placeholder={PLACEHOLDER}
                onChange={handleChange}
                aria-label={PLACEHOLDER}
              />
            )}
          </div>
        </Slot>
      </Slot>
    );
  },
);

export default ImageCaption;
